import React, { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import {
    ArrowClockwise,
    Buildings,
    CloudArrowUpFill,
    Diagram3,
    FileEarmarkText,
    InfoCircleFill,
    JournalText,
    Lightbulb,
    PlusCircle,
    ShieldCheck,
    Trash,
} from 'react-bootstrap-icons';

import {
    DomainFocusArea,
    DomainKnowledge,
    EnterpriseAcronymEntry,
    EnterpriseCautionEntry,
    EnterpriseConventionEntry,
    EnterpriseKnowledge,
    EnterpriseKnowledgePayload,
    EnterpriseProcessEntry,
    EnterpriseSystemEntry,
    KnowledgeDocument,
    ProjectWithRepository,
} from '../../types/project';
import { deleteKnowledgeDocument, fetchKnowledgeDocuments } from '../../api/projects';
import { formatApiError } from '../../utils/apiError';
import {
    parseEnterpriseAcronymsText,
    serializeEnterpriseAcronyms,
} from '../../utils/enterpriseAcronyms';
import './DomainKnowledgePanel.css';

const KNOWLEDGE_FILE_ACCEPT = '.pdf,.docx,.txt,.md,.markdown';
const KNOWLEDGE_FILE_TYPE_LABEL = 'PDF, DOCX, TXT, or Markdown';
const KNOWLEDGE_FILE_EXTENSIONS = new Set(['pdf', 'docx', 'txt', 'md', 'markdown']);
const KNOWLEDGE_MAX_FILE_BYTES = 25 * 1024 * 1024;

function validateKnowledgeUploadFiles(files: File[]): string | null
{
    const unsupportedFile = files.find((file) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        return !ext || !KNOWLEDGE_FILE_EXTENSIONS.has(ext);
    });
    if (unsupportedFile)
    {
        return `"${unsupportedFile.name}" is not supported. Upload ${KNOWLEDGE_FILE_TYPE_LABEL} files.`;
    }

    const emptyFile = files.find((file) => file.size === 0);
    if (emptyFile)
    {
        return `"${emptyFile.name}" is empty and cannot be uploaded.`;
    }

    const oversizedFile = files.find((file) => file.size > KNOWLEDGE_MAX_FILE_BYTES);
    if (oversizedFile)
    {
        return `"${oversizedFile.name}" exceeds 25 MB.`;
    }

    return null;
}

interface DomainKnowledgePanelProps
{
    project: ProjectWithRepository;
    knowledge?: DomainKnowledge | null;
    enterpriseKnowledge?: EnterpriseKnowledge | null;
    status?: string | null;
    lastRefreshedAt?: string | null;
    loading?: boolean;
    refreshing?: boolean;
    focusAreasSaving?: boolean;
    uploadingDocuments?: boolean;
    enterpriseLoading?: boolean;
    enterpriseSaving?: boolean;
    onRefresh?: (documentIds?: number[]) => Promise<void> | void;
    onSaveFocusAreas?: (focusAreas: DomainFocusArea[]) => Promise<void> | void;
    onUploadKnowledgeDocuments?: (files: File[]) => Promise<void> | void;
    onSaveEnterpriseKnowledge?: (payload: EnterpriseKnowledgePayload) => Promise<void> | void;
    enterpriseRefreshing?: boolean;
    enterpriseGenerating?: boolean;
    uploadingEnterpriseDocuments?: boolean;
    onRefreshEnterprise?: (documentIds?: number[]) => Promise<void>;
    onUploadEnterpriseKnowledgeDocuments?: (files: File[]) => Promise<void>;
}

interface EnterpriseFormState
{
    summary: string;
    internalSystems: string;
    acronyms: string;
    processNotes: string;
    testingNotes: string;
}

function normalizeStatus(status?: string | null): string
{
    const lower = (status || '').trim().toLowerCase();

    if (!lower)
    {
        return 'not_started';
    }

    if (lower === 'ready' || lower === 'success' || lower === 'succeeded')
    {
        return 'ready';
    }

    if (lower === 'document_pending' || lower === 'documents_pending' || lower === 'missing_documents')
    {
        return 'document_pending';
    }

    if (lower === 'processing' || lower === 'running' || lower === 'queued' || lower === 'updating')
    {
        return 'pending';
    }

    if (lower === 'complete')
    {
        return 'completed';
    }

    return lower;
}

function isEnterpriseGenerationStatus(status?: string | null): boolean
{
    const lower = (status || '').trim().toLowerCase();
    return lower === 'processing' || lower === 'running' || lower === 'queued' || lower === 'updating';
}

function isNoEnterpriseDataMessage(message?: string | null): boolean
{
    return (message || '').toLowerCase().includes('no enterprise-specific content');
}

function getStatusLabel(status: string): string
{
    if (status === 'document_pending')
    {
        return 'Document Pending';
    }

    if (status === 'not_started')
    {
        return 'Not Started';
    }

    return status.charAt(0).toUpperCase() + status.slice(1);
}

function getStatusClassName(status: string): string
{
    if (status === 'ready')
    {
        return 'dk-status dk-status-ready';
    }

    if (status === 'completed')
    {
        return 'dk-status dk-status-completed';
    }

    if (status === 'document_pending' || status === 'pending')
    {
        return 'dk-status dk-status-pending';
    }

    if (status === 'failed')
    {
        return 'dk-status dk-status-failed';
    }

    return 'dk-status dk-status-default';
}

function hasDetailedDescription(knowledge?: DomainKnowledge | null): boolean
{
    return Boolean((knowledge?.detailed_description || '').trim());
}

function resolveDisplayState(
    isRefreshing: boolean,
    hasDetailed: boolean,
    effectiveStatus: string,
    shouldExpectBackgroundGeneration: boolean
): string
{
    if (isRefreshing)
    {
        return 'pending';
    }

    if (hasDetailed)
    {
        return 'completed';
    }

    if (effectiveStatus === 'failed')
    {
        return 'failed';
    }

    if (effectiveStatus === 'not_started' && shouldExpectBackgroundGeneration)
    {
        return 'pending';
    }

    return effectiveStatus;
}

function getFocusAreaCardClass(index: number): string
{
    const cardClasses = [
        'dk-focus-card dk-focus-card-green',
        'dk-focus-card dk-focus-card-blue',
        'dk-focus-card dk-focus-card-amber',
        'dk-focus-card dk-focus-card-red',
        'dk-focus-card dk-focus-card-purple',
        'dk-focus-card dk-focus-card-cyan',
    ];

    return cardClasses[index % cardClasses.length];
}

function formatTimestamp(value?: string | null): string | null
{
    if (!value)
    {
        return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
    {
        return null;
    }

    return date.toLocaleString();
}

function getDocumentStatusLabel(status: string): string
{
    if (status === 'extracting')
    {
        return 'Processing';
    }

    return status.charAt(0).toUpperCase() + status.slice(1);
}

function getDocumentStatusClass(status: string): string
{
    if (status === 'ready')
    {
        return 'dk-source-status dk-source-status-ready';
    }

    if (status === 'failed')
    {
        return 'dk-source-status dk-source-status-failed';
    }

    return 'dk-source-status dk-source-status-pending';
}

function serializeAcronyms(entries: EnterpriseAcronymEntry[]): string
{
    return serializeEnterpriseAcronyms(entries);
}

function parseLineList(raw: string): string[]
{
    return raw
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
}

function parseAcronyms(raw: string): { entries: EnterpriseAcronymEntry[]; error?: string }
{
    return { entries: parseEnterpriseAcronymsText(raw) };
}

function hasEnterpriseContent(knowledge?: EnterpriseKnowledge | null): boolean
{
    return Boolean(
        (knowledge?.summary || '').trim()
        || (knowledge?.process_notes || '').trim()
        || (knowledge?.testing_notes || '').trim()
        || (knowledge?.internal_systems || []).length > 0
        || (knowledge?.acronyms || []).length > 0
        || (knowledge?.generated_systems || []).length > 0
        || (knowledge?.generated_acronyms || []).length > 0
        || (knowledge?.generated_processes || []).length > 0
        || (knowledge?.testing_cautions || []).length > 0
        || (knowledge?.naming_conventions || []).length > 0
    );
}

export function getEnterpriseMetricCounts(knowledge?: EnterpriseKnowledge | null): { systems: number; acronyms: number; capturedNotes: number }
{
    const manualNotesCount = [
        knowledge?.summary,
        knowledge?.process_notes,
        knowledge?.testing_notes,
    ].filter((value) => Boolean((value || '').trim())).length;

    return {
        systems: (knowledge?.internal_systems.length || 0) + (knowledge?.generated_systems.length || 0),
        acronyms: (knowledge?.acronyms.length || 0) + (knowledge?.generated_acronyms.length || 0),
        capturedNotes: manualNotesCount
            + (knowledge?.generated_processes.length || 0)
            + (knowledge?.testing_cautions.length || 0)
            + (knowledge?.naming_conventions.length || 0),
    };
}

function createEnterpriseFormState(knowledge?: EnterpriseKnowledge | null): EnterpriseFormState
{
    return {
        summary: knowledge?.summary || '',
        internalSystems: (knowledge?.internal_systems || []).join('\n'),
        acronyms: serializeAcronyms(knowledge?.acronyms || []),
        processNotes: knowledge?.process_notes || '',
        testingNotes: knowledge?.testing_notes || '',
    };
}

function cloneFocusAreas(focusAreas?: DomainFocusArea[] | null): DomainFocusArea[]
{
    return (focusAreas || []).map((item) => ({ ...item }));
}

function createCustomFocusArea(): DomainFocusArea
{
    return {
        area: '',
        priority: 'medium',
        source: 'custom',
    };
}

function getFocusAreaPriorityLabel(priority: DomainFocusArea['priority']): string
{
    return priority.charAt(0).toUpperCase() + priority.slice(1);
}

function getFocusAreaPriorityClass(priority: DomainFocusArea['priority']): string
{
    if (priority === 'high')
    {
        return 'dk-focus-priority dk-focus-priority-high';
    }

    if (priority === 'low')
    {
        return 'dk-focus-priority dk-focus-priority-low';
    }

    return 'dk-focus-priority dk-focus-priority-medium';
}

const DomainKnowledgePanel: React.FC<DomainKnowledgePanelProps> = ({
    project,
    knowledge,
    enterpriseKnowledge,
    status,
    lastRefreshedAt,
    loading = false,
    refreshing = false,
    focusAreasSaving = false,
    uploadingDocuments = false,
    enterpriseLoading = false,
    enterpriseSaving = false,
    enterpriseRefreshing = false,
    enterpriseGenerating = false,
    uploadingEnterpriseDocuments = false,
    onRefresh,
    onSaveFocusAreas,
    onUploadKnowledgeDocuments,
    onSaveEnterpriseKnowledge,
    onRefreshEnterprise,
    onUploadEnterpriseKnowledgeDocuments,
}) => {
    const effectiveStatus = normalizeStatus(status || knowledge?.status || null);
    const showRefresh = Boolean(onRefresh);
    const isRefreshing = loading || refreshing;
    const hasDetailed = hasDetailedDescription(knowledge);
    const [documentAlertDismissed, setDocumentAlertDismissed] = useState<boolean>(false);
    const [documentsLoading, setDocumentsLoading] = useState<boolean>(false);
    const [knowledgeDocs, setKnowledgeDocs] = useState<KnowledgeDocument[]>([]);
    const [selectedKnowledgeDocIds, setSelectedKnowledgeDocIds] = useState<number[]>([]);
    const [documentUploadError, setDocumentUploadError] = useState<string | null>(null);
    const [isEditingFocusAreas, setIsEditingFocusAreas] = useState<boolean>(false);
    const [focusAreasError, setFocusAreasError] = useState<string | null>(null);
    const [focusAreasDraft, setFocusAreasDraft] = useState<DomainFocusArea[]>(() => cloneFocusAreas(knowledge?.focus_areas));
    const [isEditingEnterprise, setIsEditingEnterprise] = useState<boolean>(false);
    const [enterpriseError, setEnterpriseError] = useState<string | null>(null);
    const [enterpriseForm, setEnterpriseForm] = useState<EnterpriseFormState>(() => createEnterpriseFormState(enterpriseKnowledge));
    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    const enterpriseUploadInputRef = useRef<HTMLInputElement | null>(null);
    const [enterpriseKnowledgeDocs, setEnterpriseKnowledgeDocs] = useState<KnowledgeDocument[]>([]);
    const [selectedEnterpriseDocIds, setSelectedEnterpriseDocIds] = useState<number[]>([]);
    const [enterpriseDocumentsLoading, setEnterpriseDocumentsLoading] = useState<boolean>(false);
    const [enterpriseDocumentUploadError, setEnterpriseDocumentUploadError] = useState<string | null>(null);
    const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);

    const hasKnowledgeDocs = knowledgeDocs.length > 0;
    const selectedKnowledgeDocCount = selectedKnowledgeDocIds.length;
    const selectedEnterpriseDocCount = selectedEnterpriseDocIds.length;
    const hasBaseDescription = Boolean((project.domain_description || '').trim());
    const shouldExpectBackgroundGeneration = effectiveStatus === 'not_started' && (hasBaseDescription || hasKnowledgeDocs || uploadingDocuments);
    const displayState = resolveDisplayState(isRefreshing, hasDetailed, effectiveStatus, shouldExpectBackgroundGeneration);
    const latestRefreshLabel = formatTimestamp(lastRefreshedAt || knowledge?.last_refreshed_at || null);
    const canEditFocusAreas = Boolean(onSaveFocusAreas && knowledge);
    const canEditEnterprise = Boolean(onSaveEnterpriseKnowledge);
    const sourceDocumentCount = knowledgeDocs.length;
    const glossaryCount = knowledge?.glossary.length || 0;
    const focusAreaCount = knowledge?.focus_areas.length || 0;
    const enterpriseMetricCounts = getEnterpriseMetricCounts(enterpriseKnowledge);
    const enterpriseGenerationActive = enterpriseGenerating
        || enterpriseRefreshing
        || isEnterpriseGenerationStatus(enterpriseKnowledge?.status);
    const canRefreshDomainKnowledge = !isRefreshing && (selectedKnowledgeDocCount > 0 || hasBaseDescription);
    const canRefreshEnterpriseKnowledge = !enterpriseGenerationActive && (selectedEnterpriseDocCount > 0 || hasEnterpriseContent(enterpriseKnowledge));

    useEffect(() =>
    {
        setDocumentAlertDismissed(false);
    }, [project.id, displayState]);

    useEffect(
        function syncEnterpriseFormWithData()
        {
            if (!isEditingEnterprise)
            {
                setEnterpriseForm(createEnterpriseFormState(enterpriseKnowledge));
                setEnterpriseError(null);
            }
        },
        [enterpriseKnowledge, isEditingEnterprise]
    );

    useEffect(
        function resetEnterpriseEditorOnProjectChange()
        {
            setIsEditingEnterprise(false);
            setEnterpriseError(null);
            setEnterpriseForm(createEnterpriseFormState(enterpriseKnowledge));
        },
        [enterpriseKnowledge, project.id]
    );

    useEffect(
        function syncFocusAreaDraftWithData()
        {
            if (!isEditingFocusAreas)
            {
                setFocusAreasDraft(cloneFocusAreas(knowledge?.focus_areas));
                setFocusAreasError(null);
            }
        },
        [isEditingFocusAreas, knowledge]
    );

    useEffect(
        function resetFocusAreaEditorOnProjectChange()
        {
            setIsEditingFocusAreas(false);
            setFocusAreasError(null);
            setFocusAreasDraft(cloneFocusAreas(knowledge?.focus_areas));
            setDocumentUploadError(null);
        },
        [knowledge, project.id]
    );

    useEffect(
        function loadKnowledgeDocuments()
        {
            let isMounted = true;

            async function fetchDocs(): Promise<void>
            {
                setDocumentsLoading(true);

                try
                {
                    const docs = await fetchKnowledgeDocuments(project.id, 'domain');
                    if (isMounted)
                    {
                        setKnowledgeDocs(docs);
                    }
                }
                catch
                {
                }
                finally
                {
                    if (isMounted)
                    {
                        setDocumentsLoading(false);
                    }
                }
            }

            void fetchDocs();

            return function cleanup(): void
            {
                isMounted = false;
            };
        },
        [project.id]
    );

    useEffect(
        function loadEnterpriseKnowledgeDocuments()
        {
            let isMounted = true;

            async function fetchEnterpriseDocs(): Promise<void>
            {
                setEnterpriseDocumentsLoading(true);

                try
                {
                    const docs = await fetchKnowledgeDocuments(project.id, 'enterprise');
                    if (isMounted)
                    {
                        setEnterpriseKnowledgeDocs(docs);
                    }
                }
                catch
                {
                }
                finally
                {
                    if (isMounted)
                    {
                        setEnterpriseDocumentsLoading(false);
                    }
                }
            }

            void fetchEnterpriseDocs();

            return function cleanup(): void
            {
                isMounted = false;
            };
        },
        [project.id]
    );

    useEffect(
        function syncSelectedDomainDocuments()
        {
            const allIds = knowledgeDocs.map((document) => document.id);
            const configured = Boolean(knowledge?.source_document_ids_configured);
            const selectedIds = configured ? (knowledge?.source_document_ids || []) : allIds;
            const validIds = selectedIds.filter((id) => allIds.includes(id));
            setSelectedKnowledgeDocIds(validIds);
        },
        [knowledge?.source_document_ids, knowledge?.source_document_ids_configured, knowledgeDocs]
    );

    useEffect(
        function syncSelectedEnterpriseDocuments()
        {
            const allIds = enterpriseKnowledgeDocs.map((document) => document.id);
            const configured = Boolean(enterpriseKnowledge?.source_document_ids_configured);
            const selectedIds = configured ? (enterpriseKnowledge?.source_document_ids || []) : allIds;
            const validIds = selectedIds.filter((id) => allIds.includes(id));
            setSelectedEnterpriseDocIds(validIds);
        },
        [enterpriseKnowledge?.source_document_ids, enterpriseKnowledge?.source_document_ids_configured, enterpriseKnowledgeDocs]
    );

    function handleRefresh(): void
    {
        if (!showRefresh || !canRefreshDomainKnowledge)
        {
            return;
        }

        try
        {
            const result = onRefresh?.(selectedKnowledgeDocIds);
            if (result && typeof (result as Promise<void>).then === 'function')
            {
                (result as Promise<void>).catch(() => undefined);
            }
        }
        catch
        {
        }
    }

    function handleDismissDocumentAlert(): void
    {
        setDocumentAlertDismissed(true);
    }

    function handleStartFocusAreaEdit(): void
    {
        setFocusAreasError(null);
        setFocusAreasDraft(cloneFocusAreas(knowledge?.focus_areas));
        setIsEditingFocusAreas(true);
    }

    function handleCancelFocusAreaEdit(): void
    {
        setFocusAreasError(null);
        setFocusAreasDraft(cloneFocusAreas(knowledge?.focus_areas));
        setIsEditingFocusAreas(false);
    }

    function handleFocusAreaFieldChange(
        index: number,
        field: keyof DomainFocusArea,
        value: string
    ): void
    {
        setFocusAreasError(null);
        setFocusAreasDraft((current) => current.map((item, itemIndex) =>
        {
            if (itemIndex !== index)
            {
                return item;
            }

            return {
                ...item,
                [field]: value,
            } as DomainFocusArea;
        }));
    }

    function handleAddCustomFocusArea(): void
    {
        setFocusAreasError(null);
        setIsEditingFocusAreas(true);
        setFocusAreasDraft((current) => [...current, createCustomFocusArea()]);
    }

    function handleRemoveCustomFocusArea(index: number): void
    {
        setFocusAreasError(null);
        setFocusAreasDraft((current) => current.filter((_, itemIndex) => itemIndex !== index));
    }

    async function handleSaveFocusAreas(): Promise<void>
    {
        if (!onSaveFocusAreas)
        {
            return;
        }

        const nextFocusAreas = focusAreasDraft.map((item) => ({
            area: item.area.trim(),
            priority: item.priority,
            source: item.source,
        }));

        if (nextFocusAreas.some((item) => !item.area))
        {
            setFocusAreasError('Each focus area needs text before you can save.');
            return;
        }

        try
        {
            await onSaveFocusAreas(nextFocusAreas);
            setFocusAreasError(null);
            setIsEditingFocusAreas(false);
        }
        catch
        {
        }
    }

    async function handleSelectKnowledgeDocuments(event: React.ChangeEvent<HTMLInputElement>): Promise<void>
    {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = '';

        if (!onUploadKnowledgeDocuments || selectedFiles.length === 0)
        {
            return;
        }

        setDocumentUploadError(null);

        const validationError = validateKnowledgeUploadFiles(selectedFiles);
        if (validationError)
        {
            setDocumentUploadError(validationError);
            return;
        }

        try
        {
            const previousIds = new Set(knowledgeDocs.map((document) => document.id));
            await onUploadKnowledgeDocuments(selectedFiles);
            const docs = await fetchKnowledgeDocuments(project.id, 'domain');
            setKnowledgeDocs(docs);
            const newIds = docs
                .map((document) => document.id)
                .filter((id) => !previousIds.has(id));
            setSelectedKnowledgeDocIds((current) => Array.from(new Set([...current, ...newIds])));
        }
        catch (error)
        {
            setDocumentUploadError(formatApiError(error, 'Failed to upload knowledge documents.'));
        }
    }

    function handleEnterpriseRefresh(): void
    {
        if (!onRefreshEnterprise || !canRefreshEnterpriseKnowledge)
        {
            return;
        }

        onRefreshEnterprise(selectedEnterpriseDocIds).catch(() => undefined);
    }

    async function handleSelectEnterpriseDocuments(event: React.ChangeEvent<HTMLInputElement>): Promise<void>
    {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = '';

        if (!onUploadEnterpriseKnowledgeDocuments || selectedFiles.length === 0)
        {
            return;
        }

        setEnterpriseDocumentUploadError(null);

        const validationError = validateKnowledgeUploadFiles(selectedFiles);
        if (validationError)
        {
            setEnterpriseDocumentUploadError(validationError);
            return;
        }

        try
        {
            const previousIds = new Set(enterpriseKnowledgeDocs.map((document) => document.id));
            await onUploadEnterpriseKnowledgeDocuments(selectedFiles);
            const docs = await fetchKnowledgeDocuments(project.id, 'enterprise');
            setEnterpriseKnowledgeDocs(docs);
            const newIds = docs
                .map((document) => document.id)
                .filter((id) => !previousIds.has(id));
            setSelectedEnterpriseDocIds((current) => Array.from(new Set([...current, ...newIds])));
        }
        catch (error)
        {
            setEnterpriseDocumentUploadError(formatApiError(error, 'Failed to upload enterprise documents.'));
        }
    }

    async function handleDeleteDocument(document: KnowledgeDocument, knowledgeType: 'domain' | 'enterprise'): Promise<void>
    {
        const confirmed = window.confirm(`Delete "${document.original_filename}"?`);
        if (!confirmed)
        {
            return;
        }

        if (knowledgeType === 'domain')
        {
            setDocumentUploadError(null);
        }
        else
        {
            setEnterpriseDocumentUploadError(null);
        }
        setDeletingDocumentId(document.id);

        try
        {
            await deleteKnowledgeDocument(project.id, document.id);
            const docs = await fetchKnowledgeDocuments(project.id, knowledgeType);
            if (knowledgeType === 'domain')
            {
                setKnowledgeDocs(docs);
                setSelectedKnowledgeDocIds((current) => current.filter((id) => id !== document.id));
            }
            else
            {
                setEnterpriseKnowledgeDocs(docs);
                setSelectedEnterpriseDocIds((current) => current.filter((id) => id !== document.id));
            }
        }
        catch (error)
        {
            const apiError = error as { response?: { data?: { detail?: string } }; message?: string };
            const message = apiError?.response?.data?.detail || apiError?.message || `Failed to delete ${knowledgeType} document.`;
            if (knowledgeType === 'domain')
            {
                setDocumentUploadError(message);
            }
            else
            {
                setEnterpriseDocumentUploadError(message);
            }
        }
        finally
        {
            setDeletingDocumentId(null);
        }
    }

    function toggleSelectedDocument(
        documentId: number,
        knowledgeType: 'domain' | 'enterprise',
        checked: boolean
    ): void
    {
        const setSelected = knowledgeType === 'domain' ? setSelectedKnowledgeDocIds : setSelectedEnterpriseDocIds;
        setSelected((current) =>
        {
            if (checked)
            {
                return current.includes(documentId) ? current : [...current, documentId];
            }
            return current.filter((id) => id !== documentId);
        });
    }

    function selectAllDocuments(knowledgeType: 'domain' | 'enterprise'): void
    {
        if (knowledgeType === 'domain')
        {
            setSelectedKnowledgeDocIds(knowledgeDocs.map((document) => document.id));
            return;
        }
        setSelectedEnterpriseDocIds(enterpriseKnowledgeDocs.map((document) => document.id));
    }

    function clearSelectedDocuments(knowledgeType: 'domain' | 'enterprise'): void
    {
        if (knowledgeType === 'domain')
        {
            setSelectedKnowledgeDocIds([]);
            return;
        }
        setSelectedEnterpriseDocIds([]);
    }

    function handleEnterpriseFieldChange(
        field: keyof EnterpriseFormState,
        value: string
    ): void
    {
        setEnterpriseError(null);
        setEnterpriseForm((current) => ({
            ...current,
            [field]: value,
        }));
    }

    function handleStartEnterpriseEdit(): void
    {
        setEnterpriseError(null);
        setEnterpriseForm(createEnterpriseFormState(enterpriseKnowledge));
        setIsEditingEnterprise(true);
    }

    function handleCancelEnterpriseEdit(): void
    {
        setEnterpriseError(null);
        setEnterpriseForm(createEnterpriseFormState(enterpriseKnowledge));
        setIsEditingEnterprise(false);
    }

    async function handleSaveEnterprise(): Promise<void>
    {
        if (!onSaveEnterpriseKnowledge)
        {
            return;
        }

        const parsedAcronyms = parseAcronyms(enterpriseForm.acronyms);
        if (parsedAcronyms.error)
        {
            setEnterpriseError(parsedAcronyms.error);
            return;
        }

        const payload: EnterpriseKnowledgePayload = {
            summary: enterpriseForm.summary.trim() || null,
            internal_systems: parseLineList(enterpriseForm.internalSystems),
            acronyms: parsedAcronyms.entries,
            process_notes: enterpriseForm.processNotes.trim() || null,
            testing_notes: enterpriseForm.testingNotes.trim() || null,
        };

        try
        {
            await onSaveEnterpriseKnowledge(payload);
            setEnterpriseError(null);
            setIsEditingEnterprise(false);
        }
        catch
        {
            setEnterpriseError('Failed to save enterprise knowledge. Please try again.');
        }
    }

    function renderHeroMetrics(
        metrics: Array<{ label: string; value: string }>,
        tone: 'domain' | 'enterprise'
    ): React.ReactNode
    {
        return (
            <div className={`dk-hero-metrics dk-hero-metrics-${tone}`}>
                {metrics.map((metric) => (
                    <article className="dk-hero-metric" key={metric.label}>
                        <span className="dk-hero-metric-label">{metric.label}</span>
                        <span className="dk-hero-metric-value">{metric.value}</span>
                    </article>
                ))}
            </div>
        );
    }

    function renderCardHeading(
        icon: React.ReactNode,
        title: string,
        subtitle?: string,
        tone: 'domain' | 'enterprise' | 'neutral' = 'domain'
    ): React.ReactNode
    {
        return (
            <div className="dk-card-heading">
                <span className={`dk-card-icon-shell dk-card-icon-shell-${tone}`}>
                    {icon}
                </span>
                <div className="dk-card-heading-copy">
                    <h3 className="dk-block-title">{title}</h3>
                    {subtitle && <p className="dk-card-subtitle">{subtitle}</p>}
                </div>
            </div>
        );
    }

    function renderTopMissingDocumentAlert(): React.ReactNode
    {
        const shouldShow = !documentAlertDismissed && !hasKnowledgeDocs && !documentsLoading;

        if (!shouldShow)
        {
            return null;
        }

        return (
            <div className="dk-top-alert" role="alert">
                <div className="dk-top-alert-main">
                    <span className="dk-top-alert-icon" aria-hidden="true">
                        <FileEarmarkText size={16} />
                    </span>
                    <span>
                        Upload knowledge documents (PDF, DOCX, TXT, or Markdown) on the Knowledge tab or during project creation to improve domain knowledge generation.
                    </span>
                </div>
                <button
                    type="button"
                    className="dk-top-alert-close"
                    aria-label="Dismiss"
                    onClick={handleDismissDocumentAlert}
                >
                    x
                </button>
            </div>
        );
    }

    function renderDomainHeader(): React.ReactNode
    {
        const badgeClassName = getStatusClassName(displayState);
        const statusLabel = getStatusLabel(displayState);
        const domainMetrics = [
            { label: 'SOURCE DOCS', value: String(sourceDocumentCount) },
            { label: 'GLOSSARY TERMS', value: String(glossaryCount) },
            { label: 'FOCUS AREAS', value: String(focusAreaCount) },
        ];

        return (
            <div className="dk-hero dk-hero-domain">
                <div className="dk-section-header">
                    <div className="dk-section-header-copy">
                        <span className="dk-section-kicker">AI-generated understanding</span>
                        <h2 className="dk-section-title">Domain Knowledge</h2>
                        <p className="dk-section-subtitle">
                            Generated from the business and application context plus uploaded project documents.
                        </p>
                        <div className="dk-meta-row">
                            <span className="dk-meta-pill">
                                {latestRefreshLabel ? `Last refreshed ${latestRefreshLabel}` : 'Not generated yet'}
                            </span>
                            <span className="dk-meta-text">Refresh updates generated domain knowledge only.</span>
                        </div>
                    </div>

                    <div className="dk-section-actions">
                        <span className={badgeClassName}>{statusLabel}</span>
                        {showRefresh && (
                            <Button
                                type="button"
                                className="dk-refresh-btn"
                                variant="outline-primary"
                                onClick={handleRefresh}
                                disabled={!canRefreshDomainKnowledge}
                            >
                                {isRefreshing
                                    ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                                            Refreshing...
                                        </>
                                    )
                                    : 'Refresh Generated Domain Knowledge'
                                }
                            </Button>
                        )}
                    </div>
                </div>

                <div className="dk-hero-footer">
                    {renderHeroMetrics(domainMetrics, 'domain')}

                    <div className="dk-hero-note">
                        <span className="dk-hero-note-label">
                            <ArrowClockwise size={14} /> Refresh guidance
                        </span>
                        <p className="dk-hero-note-text">
                            Upload or process project documents, then refresh to regenerate the AI view of the domain.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    function renderProgressPanel(): React.ReactNode
    {
        const progressTitle = hasDetailed || effectiveStatus !== 'not_started'
            ? 'Refreshing generated domain knowledge...'
            : 'Generating domain knowledge...';

        return (
            <section className="dk-progress-card">
                <div className="dk-progress-spinner" />
                <div className="dk-progress-title">{progressTitle}</div>
                <div className="dk-progress-track">
                    <div className="dk-progress-fill dk-progress-fill-indeterminate" />
                </div>
                <div className="dk-progress-value">This can take a moment depending on the document size.</div>
            </section>
        );
    }

    function renderInfoPanel(): React.ReactNode
    {
        let message = 'Click "Refresh Generated Domain Knowledge" to regenerate the AI summary, glossary, and focus areas from the current project context.';

        if (!hasKnowledgeDocs)
        {
            message = 'Add a domain description when creating the project, or upload knowledge documents here to seed the AI-generated domain knowledge.';
        }

        return (
            <section className="dk-info-card">
                <InfoCircleFill className="dk-info-icon" />
                <span>{message}</span>
            </section>
        );
    }

    function renderErrorPanel(): React.ReactNode
    {
        return (
            <section className="dk-error-card" role="alert">
                {knowledge?.error_message || 'Domain knowledge generation failed. Try refreshing after checking the source documents.'}
            </section>
        );
    }

    function renderSummarySection(): React.ReactNode
    {
        const detailDescription = (knowledge?.detailed_description || '').trim();

        if (!detailDescription)
        {
            return null;
        }

        return (
            <section className="dk-section-card dk-summary-card">
                <div className="dk-card-header">
                    {renderCardHeading(
                        <JournalText size={16} />,
                        'Domain Summary',
                        'Generated from the project context and uploaded material.'
                    )}
                    <span className="dk-card-tag">Generated</span>
                </div>
                <p className="dk-section-text dk-summary-text">{detailDescription}</p>
            </section>
        );
    }

    function renderSourceDocumentsSection(): React.ReactNode
    {
        return (
            <section className="dk-section-card dk-source-card-shell">
                <div className="dk-card-header dk-card-header-stack">
                    {renderCardHeading(
                        <FileEarmarkText size={16} />,
                        'Source Documents',
                        'Uploaded project documents feed generated domain knowledge.',
                        'neutral'
                    )}
                    <span className="dk-card-tag dk-card-tag-neutral">
                        {hasKnowledgeDocs ? `${selectedKnowledgeDocCount} of ${knowledgeDocs.length} selected` : 'Waiting for documents'}
                    </span>
                </div>

                <div className="dk-source-upload-row">
                    <input
                        ref={uploadInputRef}
                        type="file"
                        accept={KNOWLEDGE_FILE_ACCEPT}
                        multiple
                        className="dk-upload-input"
                        onChange={(event) =>
                        {
                            void handleSelectKnowledgeDocuments(event);
                        }}
                        disabled={!onUploadKnowledgeDocuments || uploadingDocuments}
                    />

                    <Button
                        type="button"
                        variant="outline-primary"
                        className="dk-secondary-btn"
                        onClick={() => uploadInputRef.current?.click()}
                        disabled={!onUploadKnowledgeDocuments || uploadingDocuments}
                    >
                        {uploadingDocuments
                            ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Uploading...
                                </>
                            )
                            : (
                                <>
                                    <CloudArrowUpFill size={14} className="me-2" />
                                    Upload Knowledge Documents
                                </>
                            )}
                    </Button>

                    <span className="dk-upload-hint">{KNOWLEDGE_FILE_TYPE_LABEL} files can be added here at any time.</span>
                </div>

                {documentUploadError && <div className="dk-error-card">{documentUploadError}</div>}

                {hasKnowledgeDocs && (
                    <div className="dk-source-selection-bar">
                        <span>{selectedKnowledgeDocCount} selected for generation</span>
                        <div className="dk-source-selection-actions">
                            <button type="button" onClick={() => selectAllDocuments('domain')}>Select all</button>
                            <button type="button" onClick={() => clearSelectedDocuments('domain')}>Clear selection</button>
                        </div>
                    </div>
                )}

                {documentsLoading && (
                    <div className="dk-inline-loading">
                        <Spinner animation="border" size="sm" />
                        <span>Loading knowledge documents...</span>
                    </div>
                )}

                {!documentsLoading && !hasKnowledgeDocs && (
                    <div className="dk-empty-subsection">
                        No knowledge documents uploaded yet. Upload PDF, DOCX, TXT, or Markdown files to enrich domain knowledge generation.
                    </div>
                )}

                {!documentsLoading && hasKnowledgeDocs && (
                    <div className="dk-source-list">
                        {knowledgeDocs.map((document) => (
                            <article className="dk-source-item" key={document.id}>
                                <div className="dk-source-item-main">
                                    <input
                                        type="checkbox"
                                        className="dk-source-checkbox"
                                        checked={selectedKnowledgeDocIds.includes(document.id)}
                                        onChange={(event) => toggleSelectedDocument(document.id, 'domain', event.target.checked)}
                                        aria-label={`Use ${document.original_filename} for generation`}
                                    />
                                    <span className="dk-source-icon" aria-hidden="true">
                                        <FileEarmarkText size={16} />
                                    </span>
                                    <div className="dk-source-copy">
                                        <div className="dk-source-name">{document.original_filename}</div>
                                        <div className="dk-source-meta">Knowledge document</div>
                                    </div>
                                </div>

                                <div className="dk-source-item-badges">
                                    <span className={getDocumentStatusClass(document.status)}>
                                        {getDocumentStatusLabel(document.status)}
                                    </span>
                                    <Button
                                        type="button"
                                        variant="outline-danger"
                                        size="sm"
                                        className="dk-source-delete-btn"
                                        onClick={() => { void handleDeleteDocument(document, 'domain'); }}
                                        disabled={deletingDocumentId === document.id}
                                        aria-label={`Delete ${document.original_filename}`}
                                    >
                                        {deletingDocumentId === document.id
                                            ? <Spinner as="span" animation="border" size="sm" />
                                            : <Trash size={13} />
                                        }
                                    </Button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>
        );
    }

    function renderGlossarySection(): React.ReactNode
    {
        const glossary = knowledge?.glossary || [];

        if (glossary.length === 0)
        {
            return null;
        }

        return (
            <section className="dk-section-card">
                <div className="dk-card-header">
                    {renderCardHeading(
                        <JournalText size={16} />,
                        'Glossary',
                        'Key terms identified from the generated domain view.'
                    )}
                    <span className="dk-card-tag">Generated</span>
                </div>

                <div className="dk-glossary-grid">
                    {glossary.map((item, index) => (
                        <article className="dk-glossary-card" key={`${item.term}-${index}`}>
                            <h4 className="dk-glossary-term">{item.term}</h4>
                            <p className="dk-glossary-definition">{item.definition}</p>
                            {item.context && <p className="dk-glossary-context">{item.context}</p>}
                        </article>
                    ))}
                </div>
            </section>
        );
    }

    function renderFocusAreasSection(): React.ReactNode
    {
        const focusAreas = isEditingFocusAreas ? focusAreasDraft : (knowledge?.focus_areas || []);

        if (focusAreas.length === 0 && !canEditFocusAreas)
        {
            return null;
        }

        return (
            <section className="dk-section-card dk-focus-shell">
                <div className="dk-card-header">
                    {renderCardHeading(
                        <Lightbulb size={16} />,
                        'Testing Focus Areas',
                        'Where the generated understanding suggests extra testing emphasis.'
                    )}
                    <div className="dk-focus-toolbar">
                        {!isEditingFocusAreas && focusAreas.length > 0 && (
                            <span className="dk-card-tag">Generated</span>
                        )}

                        {!isEditingFocusAreas && canEditFocusAreas && (
                            <Button type="button" variant="outline-primary" className="dk-secondary-btn" onClick={handleStartFocusAreaEdit}>
                                Edit Focus Areas
                            </Button>
                        )}

                        {isEditingFocusAreas && (
                            <>
                                <Button type="button" variant="outline-primary" className="dk-secondary-btn" onClick={handleAddCustomFocusArea} disabled={focusAreasSaving}>
                                    <PlusCircle size={14} className="me-2" />
                                    Add Custom Focus Area
                                </Button>
                                <Button type="button" variant="outline-secondary" className="dk-secondary-btn" onClick={handleCancelFocusAreaEdit} disabled={focusAreasSaving}>
                                    Cancel
                                </Button>
                                <Button type="button" variant="primary" className="dk-primary-btn" onClick={handleSaveFocusAreas} disabled={focusAreasSaving}>
                                    {focusAreasSaving
                                        ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                                Saving...
                                            </>
                                        )
                                        : 'Save Focus Areas'
                                    }
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                <p className="dk-focus-guidance">
                    High priority items should receive the deepest coverage. Custom focus areas are preserved when AI refreshes domain knowledge.
                </p>

                {focusAreasError && <div className="dk-error-card">{focusAreasError}</div>}

                {focusAreas.length === 0 && (
                    <div className="dk-empty-subsection">
                        No testing focus areas are available yet.
                        {canEditFocusAreas && !isEditingFocusAreas && (
                            <Button type="button" variant="outline-primary" className="dk-secondary-btn" onClick={handleAddCustomFocusArea}>
                                <PlusCircle size={14} className="me-2" />
                                Add Custom Focus Area
                            </Button>
                        )}
                    </div>
                )}

                {focusAreas.length > 0 && (
                    <div className="dk-focus-grid">
                        {focusAreas.map((item, index) => (
                            <article className={getFocusAreaCardClass(index)} key={`${item.source}-${index}`}>
                                <div className="dk-focus-card-header">
                                    <span className="dk-focus-card-icon" aria-hidden="true">
                                        <Lightbulb size={15} />
                                    </span>

                                    <div className="dk-focus-chip-row">
                                        <span className={getFocusAreaPriorityClass(item.priority)}>
                                            {getFocusAreaPriorityLabel(item.priority)} priority
                                        </span>
                                        {item.source === 'custom' && (
                                            <span className="dk-focus-priority dk-focus-priority-custom">Custom</span>
                                        )}
                                    </div>
                                </div>

                                {isEditingFocusAreas
                                    ? (
                                        <>
                                            <textarea
                                                className="dk-focus-input"
                                                rows={3}
                                                value={item.area}
                                                onChange={(event) => handleFocusAreaFieldChange(index, 'area', event.target.value)}
                                                disabled={focusAreasSaving}
                                                placeholder="Describe the area that needs extra testing emphasis"
                                            />
                                            <div className="dk-focus-editor-row">
                                                <select
                                                    className="dk-focus-select"
                                                    value={item.priority}
                                                    onChange={(event) => handleFocusAreaFieldChange(index, 'priority', event.target.value)}
                                                    disabled={focusAreasSaving}
                                                >
                                                    <option value="high">High</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="low">Low</option>
                                                </select>

                                                {item.source === 'custom' && (
                                                    <button
                                                        type="button"
                                                        className="dk-focus-remove-btn"
                                                        onClick={() => handleRemoveCustomFocusArea(index)}
                                                        disabled={focusAreasSaving}
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )
                                    : (
                                        <p className="dk-focus-card-text">{item.area}</p>
                                    )}
                            </article>
                        ))}
                    </div>
                )}
            </section>
        );
    }

    function renderProcessOverviewSection(): React.ReactNode
    {
        const steps = knowledge?.process_overview || [];

        if (steps.length === 0)
        {
            return null;
        }

        return (
            <section className="dk-section-card dk-process-shell">
                <div className="dk-card-header">
                    {renderCardHeading(
                        <Diagram3 size={16} />,
                        'Process Overview',
                        'Available for reference, with lower emphasis than the summary and testing focus areas.',
                        'neutral'
                    )}
                    <span className="dk-card-tag dk-card-tag-neutral">Reference</span>
                </div>

                <div className="dk-process-list">
                    {steps.map((step, index) => (
                        <article className="dk-process-card" key={`${step.name}-${index}`}>
                            <div className="dk-process-card-header">
                                <h4 className="dk-process-step-title">{step.name}</h4>
                                <span className="dk-process-step-index">{index + 1}</span>
                            </div>

                            <p className="dk-process-step-summary">{step.summary}</p>

                            {step.actors && step.actors.length > 0 && (
                                <p className="dk-process-step-meta">
                                    <span className="dk-process-step-meta-label">Actors:</span> {step.actors.join(', ')}
                                </p>
                            )}

                            {step.inputs && step.inputs.length > 0 && (
                                <p className="dk-process-step-meta">
                                    <span className="dk-process-step-meta-label">Inputs:</span> {step.inputs.join(', ')}
                                </p>
                            )}

                            {step.outputs && step.outputs.length > 0 && (
                                <p className="dk-process-step-meta">
                                    <span className="dk-process-step-meta-label">Outputs:</span> {step.outputs.join(', ')}
                                </p>
                            )}

                            {step.notes && (
                                <p className="dk-process-step-meta">
                                    <span className="dk-process-step-meta-label">Notes:</span> {step.notes}
                                </p>
                            )}
                        </article>
                    ))}
                </div>
            </section>
        );
    }

    function renderDomainBody(): React.ReactNode
    {
        const shouldShowInfo = !hasDetailed && displayState !== 'pending';

        return (
            <div className="dk-section-body">
                {displayState === 'pending' && renderProgressPanel()}
                {effectiveStatus === 'failed' && renderErrorPanel()}
                {renderSummarySection()}
                {renderGlossarySection()}
                {renderFocusAreasSection()}
                {renderSourceDocumentsSection()}
                {renderProcessOverviewSection()}
                {shouldShowInfo && renderInfoPanel()}
            </div>
        );
    }

    function renderEnterpriseProgressPanel(): React.ReactNode
    {
        return (
            <section className="dk-progress-card">
                <div className="dk-progress-spinner" />
                <div className="dk-progress-title">Generating enterprise knowledge from documents...</div>
                <div className="dk-progress-track">
                    <div className="dk-progress-fill dk-progress-fill-indeterminate" />
                </div>
                <div className="dk-progress-value">This can take a moment depending on the document size.</div>
            </section>
        );
    }

    function renderEnterpriseErrorPanel(): React.ReactNode
    {
        const isNoDataWarning = isNoEnterpriseDataMessage(enterpriseKnowledge?.error_message);
        const message = isNoDataWarning
            ? 'No enterprise-specific knowledge was found in the uploaded documents. You can add internal systems, rules, acronyms, URLs, or testing notes manually.'
            : enterpriseKnowledge?.error_message || 'Enterprise knowledge generation failed. Try refreshing after checking the source documents.';

        return (
            <section className={isNoDataWarning ? 'dk-warning-card' : 'dk-error-card'} role={isNoDataWarning ? 'status' : 'alert'}>
                {message}
            </section>
        );
    }

    function renderEnterpriseSourceDocumentsSection(): React.ReactNode
    {
        const hasEnterpriseDocs = enterpriseKnowledgeDocs.length > 0;

        return (
            <section className="dk-section-card dk-source-card-shell">
                <div className="dk-card-header dk-card-header-stack">
                    {renderCardHeading(
                        <FileEarmarkText size={16} />,
                        'Enterprise Source Documents',
                        'Uploaded documents feed AI-generated enterprise knowledge.',
                        'enterprise'
                    )}
                    <span className="dk-card-tag dk-card-tag-enterprise">
                        {hasEnterpriseDocs ? `${selectedEnterpriseDocCount} of ${enterpriseKnowledgeDocs.length} selected` : 'Waiting for documents'}
                    </span>
                </div>

                <div className="dk-source-upload-row">
                    <input
                        ref={enterpriseUploadInputRef}
                        type="file"
                        accept={KNOWLEDGE_FILE_ACCEPT}
                        multiple
                        className="dk-upload-input"
                        onChange={(event) =>
                        {
                            void handleSelectEnterpriseDocuments(event);
                        }}
                        disabled={!onUploadEnterpriseKnowledgeDocuments || uploadingEnterpriseDocuments}
                    />

                    <Button
                        type="button"
                        variant="outline-primary"
                        className="dk-secondary-btn"
                        onClick={() => enterpriseUploadInputRef.current?.click()}
                        disabled={!onUploadEnterpriseKnowledgeDocuments || uploadingEnterpriseDocuments}
                    >
                        {uploadingEnterpriseDocuments
                            ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Uploading...
                                </>
                            )
                            : (
                                <>
                                    <CloudArrowUpFill size={14} className="me-2" />
                                    Upload Enterprise Documents
                                </>
                            )}
                    </Button>

                    <span className="dk-upload-hint">{KNOWLEDGE_FILE_TYPE_LABEL} files can be added here at any time.</span>
                </div>

                {enterpriseDocumentUploadError && <div className="dk-error-card">{enterpriseDocumentUploadError}</div>}

                {hasEnterpriseDocs && (
                    <div className="dk-source-selection-bar">
                        <span>{selectedEnterpriseDocCount} selected for generation</span>
                        <div className="dk-source-selection-actions">
                            <button type="button" onClick={() => selectAllDocuments('enterprise')}>Select all</button>
                            <button type="button" onClick={() => clearSelectedDocuments('enterprise')}>Clear selection</button>
                        </div>
                    </div>
                )}

                {enterpriseDocumentsLoading && (
                    <div className="dk-inline-loading">
                        <Spinner animation="border" size="sm" />
                        <span>Loading enterprise documents...</span>
                    </div>
                )}

                {!enterpriseDocumentsLoading && !hasEnterpriseDocs && (
                    <div className="dk-empty-subsection">
                        No enterprise documents uploaded yet. Upload PDF, DOCX, TXT, or Markdown files to enable AI-generated enterprise knowledge.
                    </div>
                )}

                {!enterpriseDocumentsLoading && hasEnterpriseDocs && (
                    <div className="dk-source-list">
                        {enterpriseKnowledgeDocs.map((document) => (
                            <article className="dk-source-item" key={document.id}>
                                <div className="dk-source-item-main">
                                    <input
                                        type="checkbox"
                                        className="dk-source-checkbox"
                                        checked={selectedEnterpriseDocIds.includes(document.id)}
                                        onChange={(event) => toggleSelectedDocument(document.id, 'enterprise', event.target.checked)}
                                        aria-label={`Use ${document.original_filename} for generation`}
                                    />
                                    <span className="dk-source-icon" aria-hidden="true">
                                        <FileEarmarkText size={16} />
                                    </span>
                                    <div className="dk-source-copy">
                                        <div className="dk-source-name">{document.original_filename}</div>
                                        <div className="dk-source-meta">Enterprise document</div>
                                    </div>
                                </div>

                                <div className="dk-source-item-badges">
                                    <span className={getDocumentStatusClass(document.status)}>
                                        {getDocumentStatusLabel(document.status)}
                                    </span>
                                    <Button
                                        type="button"
                                        variant="outline-danger"
                                        size="sm"
                                        className="dk-source-delete-btn"
                                        onClick={() => { void handleDeleteDocument(document, 'enterprise'); }}
                                        disabled={deletingDocumentId === document.id}
                                        aria-label={`Delete ${document.original_filename}`}
                                    >
                                        {deletingDocumentId === document.id
                                            ? <Spinner as="span" animation="border" size="sm" />
                                            : <Trash size={13} />
                                        }
                                    </Button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>
        );
    }

    function renderEnterpriseView(): React.ReactNode
    {
        const hasContent = hasEnterpriseContent(enterpriseKnowledge);
        const enterpriseStatus = normalizeStatus(enterpriseKnowledge?.status ?? null);

        if (enterpriseLoading && !hasContent && !enterpriseGenerationActive)
        {
            return (
                <div className="dk-empty-state">
                    <Spinner animation="border" size="sm" />
                    <span>Loading enterprise knowledge...</span>
                </div>
            );
        }

        if (!hasContent)
        {
            if (enterpriseGenerationActive)
            {
                return (
                    <div className="dk-section-body">
                        {renderEnterpriseProgressPanel()}
                        {renderEnterpriseSourceDocumentsSection()}
                    </div>
                );
            }

            if (enterpriseStatus === 'failed')
            {
                return (
                    <div className="dk-section-body">
                        {renderEnterpriseErrorPanel()}
                        {canEditEnterprise && (
                            <Button type="button" variant="outline-primary" className="dk-secondary-btn" onClick={handleStartEnterpriseEdit}>
                                Add Enterprise Knowledge Manually
                            </Button>
                        )}
                        {renderEnterpriseSourceDocumentsSection()}
                    </div>
                );
            }

            return (
                <div className="dk-empty-state dk-empty-state-enterprise">
                    <div className="dk-empty-visual" aria-hidden="true">
                        <Buildings size={22} />
                    </div>
                    <span className="dk-empty-badge">Upload docs and refresh to generate</span>
                    <h3 className="dk-empty-title">No enterprise knowledge yet</h3>
                    <p className="dk-empty-copy">
                        Upload enterprise documents and click "Refresh Enterprise Knowledge" to have AI extract systems, acronyms, processes and cautions. You can also manually add knowledge below.
                    </p>
                    {canEditEnterprise && (
                        <Button type="button" variant="outline-primary" className="dk-secondary-btn" onClick={handleStartEnterpriseEdit}>
                            Add Enterprise Knowledge Manually
                        </Button>
                    )}
                    {renderEnterpriseSourceDocumentsSection()}
                </div>
            );
        }

        const isEnterpriseProcessing = enterpriseGenerationActive;
        const isEnterpriseFailed = enterpriseStatus === 'failed';

        return (
            <div className="dk-section-body">
                {isEnterpriseProcessing && renderEnterpriseProgressPanel()}
                {isEnterpriseFailed && renderEnterpriseErrorPanel()}

                {enterpriseKnowledge?.summary && (
                    <section className="dk-section-card dk-summary-card dk-summary-card-enterprise">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <Buildings size={16} />,
                                'Enterprise Overview',
                                'Private team context that stays separate from AI refreshes.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag dk-card-tag-enterprise">Team maintained</span>
                        </div>
                        <p className="dk-section-text dk-summary-text">{enterpriseKnowledge.summary}</p>
                    </section>
                )}

                {enterpriseKnowledge?.internal_systems && enterpriseKnowledge.internal_systems.length > 0 && (
                    <section className="dk-section-card">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <Buildings size={16} />,
                                'Internal Systems',
                                'Named platforms and tools the team relies on internally.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag dk-card-tag-enterprise">Editable</span>
                        </div>
                        <div className="dk-system-grid">
                            {enterpriseKnowledge.internal_systems.map((item, index) => (
                                <article className="dk-system-card" key={`${item}-${index}`}>
                                    <span className="dk-system-card-index">{index + 1}</span>
                                    <span className="dk-system-card-text">{item}</span>
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {enterpriseKnowledge?.acronyms && enterpriseKnowledge.acronyms.length > 0 && (
                    <section className="dk-section-card">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <JournalText size={16} />,
                                'Enterprise Acronyms',
                                'Internal vocabulary and team-specific shorthand.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag dk-card-tag-enterprise">Editable</span>
                        </div>
                        <div className="dk-glossary-grid">
                            {enterpriseKnowledge.acronyms.map((item, index) => (
                                <article className="dk-glossary-card dk-glossary-card-enterprise" key={`${item.term}-${index}`}>
                                    <h4 className="dk-glossary-term">{item.term}</h4>
                                    <p className="dk-glossary-definition">{item.definition}</p>
                                    {item.context && <p className="dk-glossary-context">{item.context}</p>}
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {enterpriseKnowledge?.process_notes && (
                    <section className="dk-section-card">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <Diagram3 size={16} />,
                                'Internal Process Notes',
                                'Approvals, handoffs, and organization-specific operating rules.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag dk-card-tag-enterprise">Editable</span>
                        </div>
                        <p className="dk-section-text">{enterpriseKnowledge.process_notes}</p>
                    </section>
                )}

                {enterpriseKnowledge?.testing_notes && (
                    <section className="dk-section-card">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <ShieldCheck size={16} />,
                                'Testing Notes and Cautions',
                                'Private constraints, environments, and testing guidance for the team.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag dk-card-tag-enterprise">Editable</span>
                        </div>
                        <p className="dk-section-text">{enterpriseKnowledge.testing_notes}</p>
                    </section>
                )}

                {/* AI-generated systems */}
                {enterpriseKnowledge?.generated_systems && enterpriseKnowledge.generated_systems.length > 0 && (
                    <section className="dk-section-card">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <Buildings size={16} />,
                                'AI-Identified Systems',
                                'Systems and platforms extracted from your enterprise documents.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag">Generated</span>
                        </div>
                        <div className="dk-system-grid">
                            {enterpriseKnowledge.generated_systems.map((item: EnterpriseSystemEntry, index: number) => (
                                <article className="dk-process-card" key={`${item.name}-${index}`}>
                                    <div className="dk-process-card-header">
                                        <h4 className="dk-process-step-title">{item.name}</h4>
                                        <span className="dk-process-step-index">{index + 1}</span>
                                    </div>
                                    <p className="dk-process-step-summary">{item.purpose}</p>
                                    {item.test_implications && (
                                        <p className="dk-process-step-meta">
                                            <span className="dk-process-step-meta-label">Testing:</span> {item.test_implications}
                                        </p>
                                    )}
                                    {item.source && (
                                        <p className="dk-process-step-meta">
                                            <span className="dk-process-step-meta-label">Source:</span> {item.source}
                                        </p>
                                    )}
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {/* AI-generated acronyms */}
                {enterpriseKnowledge?.generated_acronyms && enterpriseKnowledge.generated_acronyms.length > 0 && (
                    <section className="dk-section-card">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <JournalText size={16} />,
                                'AI-Identified Acronyms',
                                'Terms and acronyms extracted from your enterprise documents.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag">Generated</span>
                        </div>
                        <div className="dk-glossary-grid">
                            {enterpriseKnowledge.generated_acronyms.map((item: { term: string; definition: string; test_relevance: string; source: string }, index: number) => (
                                <article className="dk-glossary-card dk-glossary-card-enterprise" key={`${item.term}-${index}`}>
                                    <h4 className="dk-glossary-term">{item.term}</h4>
                                    <p className="dk-glossary-definition">{item.definition}</p>
                                    {item.test_relevance && <p className="dk-glossary-context">{item.test_relevance}</p>}
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {/* AI-generated processes */}
                {enterpriseKnowledge?.generated_processes && enterpriseKnowledge.generated_processes.length > 0 && (
                    <section className="dk-section-card dk-process-shell">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <Diagram3 size={16} />,
                                'AI-Identified Processes',
                                'Business processes and workflows found in your enterprise documents.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag">Generated</span>
                        </div>
                        <div className="dk-process-list">
                            {enterpriseKnowledge.generated_processes.map((item: EnterpriseProcessEntry, index: number) => (
                                <article className="dk-process-card" key={`${item.name}-${index}`}>
                                    <div className="dk-process-card-header">
                                        <h4 className="dk-process-step-title">{item.name}</h4>
                                        <span className="dk-process-step-index">{index + 1}</span>
                                    </div>
                                    <p className="dk-process-step-summary">{item.description}</p>
                                    {item.approval_chain && (
                                        <p className="dk-process-step-meta">
                                            <span className="dk-process-step-meta-label">Approvals:</span> {item.approval_chain}
                                        </p>
                                    )}
                                    {item.test_implications && (
                                        <p className="dk-process-step-meta">
                                            <span className="dk-process-step-meta-label">Testing:</span> {item.test_implications}
                                        </p>
                                    )}
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {/* AI-generated testing cautions */}
                {enterpriseKnowledge?.testing_cautions && enterpriseKnowledge.testing_cautions.length > 0 && (
                    <section className="dk-section-card">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <ShieldCheck size={16} />,
                                'AI-Identified Testing Cautions',
                                'Testing risks and cautions identified from your enterprise documents.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag">Generated</span>
                        </div>
                        <div className="dk-process-list">
                            {enterpriseKnowledge.testing_cautions.map((item: EnterpriseCautionEntry, index: number) => (
                                <article className="dk-process-card" key={`${item.caution}-${index}`}>
                                    <div className="dk-process-card-header">
                                        <h4 className="dk-process-step-title">{item.category}</h4>
                                    </div>
                                    <p className="dk-process-step-summary">{item.caution}</p>
                                    {item.test_implications && (
                                        <p className="dk-process-step-meta">
                                            <span className="dk-process-step-meta-label">Testing:</span> {item.test_implications}
                                        </p>
                                    )}
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {/* AI-generated naming conventions */}
                {enterpriseKnowledge?.naming_conventions && enterpriseKnowledge.naming_conventions.length > 0 && (
                    <section className="dk-section-card">
                        <div className="dk-card-header">
                            {renderCardHeading(
                                <JournalText size={16} />,
                                'Naming Conventions',
                                'Coding and naming standards found in your enterprise documents.',
                                'enterprise'
                            )}
                            <span className="dk-card-tag">Generated</span>
                        </div>
                        <div className="dk-process-list">
                            {enterpriseKnowledge.naming_conventions.map((item: EnterpriseConventionEntry, index: number) => (
                                <article className="dk-process-card" key={`${item.convention}-${index}`}>
                                    <p className="dk-process-step-summary">{item.convention}</p>
                                    {item.example && (
                                        <p className="dk-process-step-meta">
                                            <span className="dk-process-step-meta-label">Example:</span> {item.example}
                                        </p>
                                    )}
                                    {item.test_implications && (
                                        <p className="dk-process-step-meta">
                                            <span className="dk-process-step-meta-label">Testing:</span> {item.test_implications}
                                        </p>
                                    )}
                                </article>
                            ))}
                        </div>
                    </section>
                )}

                {renderEnterpriseSourceDocumentsSection()}
            </div>
        );
    }

    function renderEnterpriseEditor(): React.ReactNode
    {
        return (
            <div className="dk-section-body">
                <section className="dk-section-card dk-form-card">
                    <div className="dk-editor-banner">
                        <span className="dk-editor-banner-kicker">Human-maintained</span>
                        <p className="dk-editor-banner-copy">
                            Capture private internal systems, acronyms, workflows, and cautions here. AI refreshes do not change this content.
                        </p>
                    </div>

                    <div className="dk-form-grid">
                        <label className="dk-form-field dk-form-field-wide">
                            <span className="dk-form-label">Overview</span>
                            <textarea
                                className="dk-textarea dk-textarea-lg"
                                rows={4}
                                value={enterpriseForm.summary}
                                onChange={(event) => handleEnterpriseFieldChange('summary', event.target.value)}
                                placeholder="Describe the internal context the team should preserve for this project."
                                disabled={enterpriseSaving}
                            />
                        </label>

                        <label className="dk-form-field">
                            <span className="dk-form-label">Internal Systems</span>
                            <textarea
                                className="dk-textarea"
                                rows={5}
                                value={enterpriseForm.internalSystems}
                                onChange={(event) => handleEnterpriseFieldChange('internalSystems', event.target.value)}
                                placeholder="List one system per line"
                                disabled={enterpriseSaving}
                            />
                            <span className="dk-form-hint">Use one line per system or internal platform.</span>
                        </label>

                        <label className="dk-form-field">
                            <span className="dk-form-label">Acronyms and Enterprise Glossary</span>
                            <textarea
                                className="dk-textarea"
                                rows={6}
                                value={enterpriseForm.acronyms}
                                onChange={(event) => handleEnterpriseFieldChange('acronyms', event.target.value)}
                                placeholder={'CRM: Customer relationship management\nRPA - Robotic process automation\nSLA, Service-level agreement'}
                                disabled={enterpriseSaving}
                            />
                            <span className="dk-form-hint">Paste one acronym or internal term per line. Colons, dashes, commas, and tabs are accepted.</span>
                        </label>

                        <label className="dk-form-field">
                            <span className="dk-form-label">Internal Process Notes</span>
                            <textarea
                                className="dk-textarea"
                                rows={5}
                                value={enterpriseForm.processNotes}
                                onChange={(event) => handleEnterpriseFieldChange('processNotes', event.target.value)}
                                placeholder="Capture internal workflows, approvals, and handoffs that affect testing."
                                disabled={enterpriseSaving}
                            />
                        </label>

                        <label className="dk-form-field">
                            <span className="dk-form-label">Testing Notes and Cautions</span>
                            <textarea
                                className="dk-textarea"
                                rows={5}
                                value={enterpriseForm.testingNotes}
                                onChange={(event) => handleEnterpriseFieldChange('testingNotes', event.target.value)}
                                placeholder="Document non-public testing constraints, environments, and cautions."
                                disabled={enterpriseSaving}
                            />
                        </label>
                    </div>

                    {enterpriseError && <div className="dk-error-card">{enterpriseError}</div>}
                </section>
            </div>
        );
    }

    function renderEnterpriseHeader(): React.ReactNode
    {
        const normalizedEnterpriseStatus = normalizeStatus(enterpriseKnowledge?.status ?? null);
        const hasNoDataWarning = normalizedEnterpriseStatus === 'failed'
            && isNoEnterpriseDataMessage(enterpriseKnowledge?.error_message);
        const enterpriseStatus = hasEnterpriseContent(enterpriseKnowledge)
            && normalizedEnterpriseStatus === 'pending'
            && !enterpriseGenerationActive
            ? 'ready'
            : hasNoDataWarning
                ? 'not_started'
                : normalizedEnterpriseStatus;
        const enterpriseStatusLabel = getStatusLabel(enterpriseStatus);
        const enterpriseStatusClass = getStatusClassName(enterpriseStatus);
        const enterpriseLastRefreshed = formatTimestamp(enterpriseKnowledge?.last_refreshed_at ?? null);
        const enterpriseMetrics = [
            { label: 'Systems', value: String(enterpriseMetricCounts.systems) },
            { label: 'Acronyms', value: String(enterpriseMetricCounts.acronyms) },
            { label: 'Captured notes', value: String(enterpriseMetricCounts.capturedNotes) },
        ];

        return (
            <div className="dk-hero dk-hero-enterprise">
                <div className="dk-section-header">
                    <div className="dk-section-header-copy">
                        <span className="dk-section-kicker dk-section-kicker-enterprise">Team-maintained context</span>
                        <h2 className="dk-section-title">Enterprise Knowledge</h2>
                        <p className="dk-section-subtitle">
                            Internal systems, acronyms, workflows, and organization-specific testing notes that should stay human maintained.
                        </p>
                        <div className="dk-meta-row">
                            <span className="dk-meta-pill">
                                {enterpriseLastRefreshed ? `Last refreshed ${enterpriseLastRefreshed}` : 'Not generated yet'}
                            </span>
                        </div>
                    </div>

                    <div className="dk-section-actions">
                        {enterpriseKnowledge?.status && (
                            <span className={enterpriseStatusClass}>{enterpriseStatusLabel}</span>
                        )}
                        {onRefreshEnterprise && (
                            <Button
                                type="button"
                                className="dk-refresh-btn"
                                variant="outline-primary"
                                onClick={handleEnterpriseRefresh}
                                disabled={!canRefreshEnterpriseKnowledge}
                            >
                                {enterpriseGenerationActive
                                    ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                                            {enterpriseRefreshing ? 'Refreshing...' : 'Generating...'}
                                        </>
                                    )
                                    : 'Refresh Enterprise Knowledge'
                                }
                            </Button>
                        )}
                        {!isEditingEnterprise && canEditEnterprise && (hasEnterpriseContent(enterpriseKnowledge) || hasNoDataWarning) && (
                            <Button type="button" variant="outline-primary" className="dk-secondary-btn" onClick={handleStartEnterpriseEdit}>
                                {hasEnterpriseContent(enterpriseKnowledge) ? 'Edit Enterprise Knowledge' : 'Add Enterprise Knowledge Manually'}
                            </Button>
                        )}

                        {isEditingEnterprise && (
                            <>
                                <Button type="button" variant="outline-secondary" className="dk-secondary-btn" onClick={handleCancelEnterpriseEdit} disabled={enterpriseSaving}>
                                    Cancel
                                </Button>
                                <Button type="button" variant="primary" className="dk-primary-btn" onClick={handleSaveEnterprise} disabled={enterpriseSaving}>
                                    {enterpriseSaving
                                        ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                                Saving...
                                            </>
                                        )
                                        : 'Save Enterprise Knowledge'
                                    }
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                <div className="dk-hero-footer">
                    {renderHeroMetrics(enterpriseMetrics, 'enterprise')}

                    <div className="dk-hero-note dk-hero-note-enterprise">
                        <span className="dk-hero-note-label">
                            <Buildings size={14} /> Ownership boundary
                        </span>
                        <p className="dk-hero-note-text">
                            Upload enterprise documents and refresh to have AI extract systems, acronyms, processes, and cautions.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dk-panel">
            {renderTopMissingDocumentAlert()}

            <div className="dk-workspace-grid">
                <section className="dk-workspace-section dk-workspace-section-domain">
                    {renderDomainHeader()}
                    {renderDomainBody()}
                </section>

                <section className="dk-workspace-section dk-workspace-section-enterprise">
                    {renderEnterpriseHeader()}
                    {isEditingEnterprise ? renderEnterpriseEditor() : renderEnterpriseView()}
                </section>
            </div>
        </div>
    );
};

export default DomainKnowledgePanel;
